import {chunk} from '@benchling/js-utils';

import * as Analytics from 'analytics/Analytics';
import {standardAnalyticsMetadataToDict} from 'analytics/Util';
import {AnalyticsMetadataForContext, STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {getAccessibleNonBatchContainerContents} from 'constants/SampleConstants';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';

import {PrintBarcodeModalQuery} from './__generated/PrintBarcodeModalQuery';
import {LabelPrintable, PrintableExtras} from './LabelPrintableSummary';
import {LabelPrintingMethod} from './LabelPrintingConstants';
import {bulkGetFilledLabelTemplate, logPrint} from './LabelPrintingDAO';
import {ZebraBrowserPrintException, ZebraDevice} from './ZebraBrowserPrint';

type PrintableContainer = ExtractByTypename<PrintBarcodeModalQuery['storables'][number], 'ContainerNode'>;

type ContainerContent = PrintableContainer['contents'][number];

type ContainedItem = ExcludeInaccessible<ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>>;

interface AccessibleContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

// Keep in sync with PRINTER_CHUNK_SIZE in benchling/label_printing/chunk.py.
const PRINTER_CHUNK_SIZE = 20;

function isPrintableContainer(
  printableItem: LabelPrintable
): printableItem is PrintableContainer & PrintableExtras {
  return printableItem.__typename === 'ContainerNode';
}

function trackAnalyticsEvents({
  printableItems,
  device,
  labelTemplateId,
  copies,
  successfulItemsCount,
  errorMessage,
  tenantId,
  currentUserId,
  analyticsMetadata,
}: {
  printableItems: Array<LabelPrintable>;
  device: ZebraDevice;
  labelTemplateId: string;
  copies: number;
  successfulItemsCount: number;
  errorMessage: string | null;
  tenantId: string;
  currentUserId: string;
  analyticsMetadata: AnalyticsMetadataForContext;
}): void {
  const eventGroupId =
    analyticsMetadata?.eventGroupId ?? Analytics.deprecatedGenerateEventGroupId(tenantId, currentUserId);

  for (const [i, printableItem] of printableItems.entries()) {
    const outcome =
      i < successfulItemsCount
        ? 'success'
        : i === successfulItemsCount
          ? 'failed'
          : 'not_attempted_prior_failure';

    const containedItem =
      isPrintableContainer(printableItem) && printableItem.contents.length > 0
        ? getAccessibleNonBatchContainerContents<ContainerContent, AccessibleContentWithoutBatches>(
            printableItem.contents
          )[0]?.containedItem
        : null;

    Analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.INITIATED_LABEL_PRINT, {
      ...standardAnalyticsMetadataToDict(analyticsMetadata),
      event_group_id: eventGroupId,
      print_method: LabelPrintingMethod.ZEBRA_BROWSER_PRINT,
      printer_name: device.name,
      // label_template_name -- n/a for Zebra browser print
      label_template_id: labelTemplateId,
      num_copies: copies,
      item_id: printableItem.id,
      item_type: APIUtil.apiIdToType(printableItem.id),
      contained_item_id: containedItem?.id,
      contained_item_type: containedItem ? APIUtil.apiIdToType(containedItem.id) : null,
      num_contained_entities: isPrintableContainer(printableItem) ? printableItem.contents.length : null,
      outcome,
      error_message: outcome === 'failed' ? errorMessage : null,
      // template_fields TODO(BNCH-42640)
      // printer_server -- n/a for Zebra browser print
      // printer_id -- n/a for Zebra browser print
    });
  }
}

export async function printZebraLabels({
  printableItems,
  labelTemplateId,
  labelTemplateName,
  device,
  tenantId,
  currentUserId,
  copies = 1,
  analyticsMetadata = {},
}: {
  printableItems: Array<LabelPrintable>;
  labelTemplateId: string;
  labelTemplateName: string;
  device: ZebraDevice;
  tenantId: string;
  currentUserId: string;
  copies: number;
  analyticsMetadata: AnalyticsMetadataForContext;
}): Promise<void> {
  const itemIds = printableItems.map((p) => p.id);
  const {printPayloads} = await bulkGetFilledLabelTemplate(itemIds, labelTemplateId);
  const contiguousCopiesPayload = printPayloads.flatMap((payload) => Array(copies).fill(payload));
  let successfulCopiesCount = 0;
  let errorMessage = null;

  try {
    for (const zplDocumentBatch of chunk(contiguousCopiesPayload, PRINTER_CHUNK_SIZE)) {
      await device.send(zplDocumentBatch.join('\n'));
      successfulCopiesCount += zplDocumentBatch.length;
    }
    notifications.success(`Labels sent to printer ${device.name}`);
  } catch (error) {
    if (!(error instanceof ZebraBrowserPrintException)) {
      errorMessage = String(error).toString();
      throw error;
    }
    logging.warn(`ZebraBrowserPrintException: ${error.message}`);
    errorMessage = error.message;
    notifications.error(
      `Error when sending labels to ${device.name}: ${error.message}\n\n` +
        `Please check your network and printer configuration.`,
      {duration: 0}
    );
  } finally {
    trackAnalyticsEvents({
      printableItems,
      device,
      labelTemplateId,
      copies,
      successfulItemsCount: Math.floor(successfulCopiesCount / copies),
      errorMessage,
      tenantId,
      currentUserId,
      analyticsMetadata,
    });
    logPrint(itemIds, labelTemplateName, device.name, copies);
  }
}
